import {
  ampathSystem,
  carboneSystem,
  droppaSystem,
  fhirObservationInterpretationSystem,
  iconoirSystem,
  lancetSystem,
  loincSystem,
  medpraxSystem,
  system,
  twilioSystem,
} from '@quromedical/fhir-common';
import { z } from 'zod';
import { MediaDataSchema } from '../media/base';

export const systemSchema = z.enum([
  system,
  medpraxSystem,
  ampathSystem,
  lancetSystem,
  droppaSystem,
  loincSystem,
  fhirObservationInterpretationSystem,
  twilioSystem,
  iconoirSystem,
  carboneSystem,
]);

export const DateTimeSchema = z.string().datetime();

export const RequiredCodingSchema = z.object({
  system: z.string(),
  code: z.string(),
  display: z.string().optional(),
  version: z.string().optional(),
});

export const CodingSchema = z.object({
  code: z.string(),
  display: z.string().optional(),
  system: z.string().optional(),
  version: z.string().optional(),
});

export const DisplayCodeableConceptSchema = z.object({
  coding: z.array(CodingSchema),
  text: z.string().optional(),
});

export const BundleLinkSchema = z.object({
  relation: z.string(),
  url: z.string(),
});

export const PaginatedBundleSchema = z.object({
  link: BundleLinkSchema.array().optional(),
});

export const PartialPeriodSchema = z.object({
  start: z.string().optional(),
  end: z.string().optional(),
});

// TODO: this should be a date
export const PeriodSchema = z.object({
  start: z.string(),
  end: z.string().optional(),
});

export const PaginatedResponseSchema = z.object({
  items: z.any().array(),
  link: BundleLinkSchema.array().optional(),
});

export const WithIdSchema = z.object({ id: z.string() });

export const dateSchema = z.union([z.string().pipe(z.coerce.date()), z.date()]);

export const comparatorSchema = z.enum(['<', '<=', '>=', '>']);

export const QuantitySchema = z.object({
  value: z.number().optional(),
  unit: z.string().optional(),
  system: z.string().optional(),
  code: z.string().optional(),
  comparator: comparatorSchema.optional(),
});

export type Coding = z.infer<typeof CodingSchema>;

export type DisplayCodeableConcept = z.infer<
  typeof DisplayCodeableConceptSchema
>;

export type AggregateData<TData, TErr = any> =
  | { type: 'error'; error: TErr }
  | { type: 'success'; data: TData };

export type PaginatedBundle = z.infer<typeof PaginatedBundleSchema>;

export type PaginatedResponse = z.infer<typeof PaginatedResponseSchema>;

export const RangeSchema = z.object({
  low: QuantitySchema.optional(),
  high: QuantitySchema.optional(),
});

export const RatioSchema = z.object({
  numerator: QuantitySchema.optional(),
  denominator: QuantitySchema.optional(),
});

export const SampleDataSchema = z.object({
  origin: QuantitySchema,
  period: z.number(),
  factor: z.number().optional(),
  lowerLimit: z.number().optional(),
  upperLimit: z.number().optional(),
  dimensions: z.number(),
  data: z.string().optional(),
});

export const ValueQuantitySchema = z.object({
  valueQuantity: QuantitySchema,
});
export type ValueQuantity = z.infer<typeof ValueQuantitySchema>;

export const ValueCodeableConceptSchema = z.object({
  valueCodeableConcept: DisplayCodeableConceptSchema,
});
export type ValueCodeableConcept = z.infer<typeof ValueCodeableConceptSchema>;

export const ValueStringSchema = z.object({
  valueString: z.string(),
});
export type ValueString = z.infer<typeof ValueStringSchema>;

export const ValueRangeSchema = z.object({
  valueRange: RangeSchema,
});
export type ValueRange = z.infer<typeof ValueRangeSchema>;

export const ValueRatioSchema = z.object({
  valueRatio: RatioSchema,
});
export type ValueRatio = z.infer<typeof ValueRatioSchema>;

export const ValueSampledDataSchema = z.object({
  valueSampledData: SampleDataSchema,
});
export type ValueSampledData = z.infer<typeof ValueSampledDataSchema>;

export const ValueAttachmentSchema = z.object({
  valueAttachment: MediaDataSchema,
});
export type ValueAttachment = z.infer<typeof ValueAttachmentSchema>;

export const ValueTimeSchema = z.object({
  valueTime: z.string(),
});
export type ValueTime = z.infer<typeof ValueTimeSchema>;

export const ValueDateTimeSchema = z.object({
  valueDateTime: z.string(),
});
export type ValueDateTime = z.infer<typeof ValueDateTimeSchema>;

export const ValuePeriodSchema = z.object({
  valuePeriod: PeriodSchema,
});
export type ValuePeriod = z.infer<typeof ValuePeriodSchema>;

export const ValueTypeSchema = ValueQuantitySchema.or(
  ValueCodeableConceptSchema,
)
  .or(ValueStringSchema)
  .or(ValueRangeSchema)
  .or(ValueRatioSchema)
  .or(ValueSampledDataSchema)
  .or(ValueAttachmentSchema)
  .or(ValueTimeSchema)
  .or(ValueDateTimeSchema)
  .or(ValuePeriodSchema);

export type ValueType = z.infer<typeof ValueTypeSchema>;

export type ValueTypeNames = {
  valueQuantity: ValueQuantity;
  valueCodeableConcept: ValueCodeableConcept;
  valueString: ValueString;
  valueRange: ValueRange;
  valueRatio: ValueRatio;
  valueSampledData: ValueSampledData;
  valueAttachment: ValueAttachment;
  valueTime: ValueTime;
  valueDateTime: ValueDateTime;
  valuePeriod: ValuePeriod;
};

export type ValueTypeRecord<T extends ValueType> = {
  toString(): string;
  toValue(): T;
};

export type ValueTypeModel<T extends ValueType> = (
  input: T,
) => ValueTypeRecord<T>;
